import { Button } from "@/components/flexkit/Button";
import { Checkbox } from "@/components/podkit/checkbox/Checkbox";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useOrganization } from "@/queries/organization-queries";
import { groupsQueryKey, useCreateRoleAssignment, useDeleteRoleAssignment, useListRoleAssignments } from "@/queries/group-queries";
import { ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { useQueryClient } from "@tanstack/react-query";
import { formatError } from "@/utils/errors";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";
import { type FC, useCallback, useMemo, useState } from "react";
import { GroupAvatar } from "@/components/settings/organization/groups/GroupAvatar";
import { TrackLocations } from "@/hooks/use-segment";

export type ManageOrgRolesDialogProps = {
    group: PlainMessage<Group>;
    open: boolean;
    onClose: () => void;
};

type OrgRoleConfig = {
    role: ResourceRole;
    label: string;
    description: string;
};

const ORG_ROLES: OrgRoleConfig[] = [
    {
        role: ResourceRole.ORG_RUNNERS_ADMIN,
        label: "Runners Admin",
        description: "Can create and manage all runners in the organization",
    },
];

export const ManageOrgRolesDialog: FC<ManageOrgRolesDialogProps> = ({ group, open, onClose }) => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { data: organization } = useOrganization();
    const orgId = organization?.id ?? "";

    const { data: roleAssignmentsData, isLoading } = useListRoleAssignments({
        resourceType: ResourceType.ORGANIZATION,
        resourceId: orgId,
        groupId: group.id,
        enabled: open && !!orgId,
    });

    const createRoleAssignment = useCreateRoleAssignment();
    const deleteRoleAssignment = useDeleteRoleAssignment();

    // Track local state for optimistic UI
    const [pendingChanges, setPendingChanges] = useState<Map<ResourceRole, boolean>>(new Map());

    const assignedRoles = useMemo(() => {
        const assignments = roleAssignmentsData?.assignments ?? [];
        return new Set(assignments.map((a) => a.resourceRole));
    }, [roleAssignmentsData?.assignments]);

    const assignmentIdByRole = useMemo(() => {
        const assignments = roleAssignmentsData?.assignments ?? [];
        const map = new Map<ResourceRole, string>();
        for (const a of assignments) {
            map.set(a.resourceRole, a.id);
        }
        return map;
    }, [roleAssignmentsData?.assignments]);

    const isRoleAssigned = useCallback(
        (role: ResourceRole) => {
            if (pendingChanges.has(role)) {
                return pendingChanges.get(role);
            }
            return assignedRoles.has(role);
        },
        [assignedRoles, pendingChanges],
    );

    const handleToggleRole = useCallback((role: ResourceRole, checked: boolean) => {
        setPendingChanges((prev) => {
            const next = new Map(prev);
            next.set(role, checked);
            return next;
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (!orgId) return;

        const changes = Array.from(pendingChanges.entries());
        let hasError = false;

        for (const [role, shouldBeAssigned] of changes) {
            const isCurrentlyAssigned = assignedRoles.has(role);
            if (shouldBeAssigned === isCurrentlyAssigned) continue;

            try {
                if (shouldBeAssigned) {
                    await createRoleAssignment.mutateAsync({
                        groupId: group.id,
                        resourceType: ResourceType.ORGANIZATION,
                        resourceId: orgId,
                        resourceRole: role,
                    });
                } else {
                    const assignmentId = assignmentIdByRole.get(role);
                    if (assignmentId) {
                        await deleteRoleAssignment.mutateAsync({
                            assignmentId,
                            resourceType: ResourceType.ORGANIZATION,
                            resourceId: orgId,
                        });
                    }
                }
            } catch (error) {
                hasError = true;
                console.error("Failed to update role assignment:", formatError(error));
            }
        }

        // Invalidate the query with groupId to refresh GroupCard's role badges
        await queryClient.invalidateQueries({
            queryKey: groupsQueryKey.roleAssignments(ResourceType.ORGANIZATION, orgId, group.id),
        });

        if (hasError) {
            toast({
                title: "Error",
                description: "Some role changes could not be saved.",
            });
        } else if (changes.length > 0) {
            toast({
                title: "Roles updated",
                description: `Organization roles for ${group.name} have been updated.`,
            });
        }

        setPendingChanges(new Map());
        onClose();
    }, [
        orgId,
        group.id,
        group.name,
        pendingChanges,
        assignedRoles,
        assignmentIdByRole,
        createRoleAssignment,
        deleteRoleAssignment,
        queryClient,
        toast,
        onClose,
    ]);

    const handleClose = useCallback(() => {
        setPendingChanges(new Map());
        onClose();
    }, [onClose]);

    const isPending = createRoleAssignment.isPending || deleteRoleAssignment.isPending;
    const hasChanges = pendingChanges.size > 0;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.SettingsGroupManageOrgRoles}>
                <DialogHeader>
                    <DialogTitle>Manage organization roles</DialogTitle>
                    <DialogDescription>
                        Assign organization-level roles to this group. Members will inherit these permissions.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    {/* Group info */}
                    <div className="mb-4 flex items-center gap-3 rounded-lg border border-border-base bg-surface-secondary p-3">
                        <GroupAvatar groupName={group.name} size="sm" />
                        <div>
                            <div className="font-medium">{group.name}</div>
                            {group.description && (
                                <div className="text-sm text-content-secondary">{group.description}</div>
                            )}
                        </div>
                    </div>

                    {/* Role options */}
                    <div className="space-y-3">
                        {ORG_ROLES.map((roleConfig) => {
                            const checked = isRoleAssigned(roleConfig.role);
                            return (
                                <div key={roleConfig.role} className="flex items-start gap-3">
                                    <Checkbox
                                        id={`role-${roleConfig.role}`}
                                        checked={checked}
                                        disabled={isLoading || isPending}
                                        onCheckedChange={(checked) => handleToggleRole(roleConfig.role, checked === true)}
                                        className="mt-0.5"
                                    />
                                    <label htmlFor={`role-${roleConfig.role}`} className="cursor-pointer">
                                        <div className="font-medium">{roleConfig.label}</div>
                                        <div className="text-sm text-content-secondary">{roleConfig.description}</div>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isPending}
                            data-tracking-id="cancel"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        onClick={handleSave}
                        disabled={isPending || !hasChanges}
                        loading={isPending}
                        data-tracking-id="save"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
